import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import store from '@/redux/store';
import styled from 'styled-components';

import SectionContainer from '@/components/Container/SectionContainer';
import Sidebar from '@/layout/Sidebar';
import Logo from './Logo';
import Navbar from './Navbar';

import { useAppSelector } from '@/redux/store.hook';
import { addUnreadCounter, getAuthSelector, signOut } from '@/redux/auth/auth.slice';
import { disconnect, getNotification } from '@/redux/notification/notification.slice';
import { getUserNotifications } from '@/api/notifications.api';
import { useDispatch } from 'react-redux';
import { useSocket } from '@/hooks/useSocket';
import { checkUserRegister } from '@/utils/checkUserRegister';
import { clearCart } from '@/redux/cart/cart.slice';
import useResize from '@/hooks/useResize';
import Userbar from '../Userbar';
import CookiePolicy from '@/components/UI/CookiePolicy';
import SEO from '@/components/SEO';
import { isPblUser } from '@/utils/pblRole';
import {
  isExternalRosterPath,
  shouldRenderDesktopSidebar,
  shouldRenderHeaderNavigation,
  shouldUseAuthenticatedLayout,
  shouldUseMobileHeader,
} from './headerLayout';
import i18n from '@/i18n';
import PricingHeader from './PricingHeader';
import { v2UiEnabled } from '@/components/DmpV2/features';

const HeaderWrapper = styled.div<{ isMobile: boolean; useMobileNavigation: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  min-height: 70px;
  margin: 0 auto;
  padding: ${(p) => (p.isMobile ? '20px' : p.useMobileNavigation ? '10px 20px' : '30px 10px')};
  position: relative;

  @media (max-width: 1450px) {
    zoom: ${(p) => (p.useMobileNavigation ? 1 : 0.9)};
  }
`;

const MobileSidebarWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9;
`;

const Header = () => {
  const location = useLocation();
  const { size, isMobile, isTablet } = useResize();
  const [layoutController, setLayoutController] = useState(false);
  const navigate = useNavigate();

  // user data
  const dispatch = useDispatch();
  const auth = useAppSelector(getAuthSelector);
  const { isLogin, userData, registDone, accessToken } = auth;
  const [searchParams, setSearchParams] = useSearchParams();
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const { pathname } = location;
  const showHeaderNavigation = shouldRenderHeaderNavigation(pathname);
  const useMobileNavigation = shouldUseMobileHeader({
    isLogin,
    isTablet,
    viewportWidth: size,
    pathname,
  });

  useSocket({
    type: 'CONNECT_NOTICE',
    callBack: (WSdata: WSNotification) => {
      if (WSdata.type === '90') {
        // 跳轉到WSdata.message傳來的外部網址
        window.location.href = WSdata.message;
      } else if (WSdata.type === '1') {
        const roomParams = searchParams.get('room') ? searchParams.get('room')! : '';
        // console.log(userData.id, WSdata.triggered_by, searchParams, WSdata.reference_id.toString());
        // 檢查是否為自己發送的訊息 & 檢查是否為當前聊天室, 2者皆否才將未讀數量+1
        if (userData.id !== WSdata.triggered_by || roomParams !== WSdata.reference_id.toString()) {
          dispatch(addUnreadCounter());
        }
      }
    },
  });

  useSocket({
    type: 'CONNECT_INBOX',
    callBack: async () => {
      try {
        const response = await getUserNotifications();
        dispatch(getNotification(response.data.data));
      } catch {
        // 下次載入頁面時再同步通知。
      }
    },
  });

  useEffect(() => {
    if (isLogin && !isPblUser(userData) && userData.stage < 4 && pathname !== '/registration') {
      if (pathname === '/auth/signin') return;
      dispatch(disconnect());
      dispatch(signOut());
      dispatch(clearCart());

      // 一秒後再跳轉
      setTimeout(() => {
        navigate('/');
      }, 1000);
    }

    setLayoutController(
      shouldUseAuthenticatedLayout({
        pathname,
        isLogin: auth.isLogin,
        userData,
      })
    );

    // 黃頁獨自拉出來做判斷
    if (pathname === '/eDirectory' && auth.isLogin) {
      setLayoutController(true);
      return;
    }

    // 律師事務所頁獨自拉出來做判斷
    if (pathname === '/professionalService' && auth.isLogin) {
      setLayoutController(true);
      return;
    }

    if (!isLogin) {
      dispatch(disconnect());
    }

    // const doneRegistration = checkUserRegister(userData);

    // console.log(doneRegistration);

    // if(isLogin && pathname !== '/registration' && !doneRegistration) {
    //   navigate('/registration', { replace: true });
    // }

    // 若登入後且在首頁，則跳轉到home
    if (pathname === '/' && isLogin && userData.acc_firstName) {
      navigate('/home', { replace: true });
    } else if (pathname === '/' && isLogin && !userData.acc_firstName) {
      dispatch(disconnect());
      dispatch(signOut());
      dispatch(clearCart());
    }
  }, [pathname, isLogin, userData]);

  useEffect(() => {
    if (showHeaderNavigation && useMobileNavigation && burgerOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';

    if (!useMobileNavigation && burgerOpen) setBurgerOpen(false);
  }, [size, burgerOpen, useMobileNavigation, showHeaderNavigation]);

  useEffect(() => {
    if (showHeaderNavigation && useMobileNavigation && userOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';

    if (!useMobileNavigation && userOpen) setUserOpen(false);
  }, [size, userOpen, useMobileNavigation, showHeaderNavigation]);

  if (v2UiEnabled() && /^\/(?:en|tc|sc)\/Subscription\/?$/.test(location.pathname)) {
    return <><SEO /><CookiePolicy /><PricingHeader /></>;
  }

  return (
    <>
      <SEO />
      <nav style={{ position: 'relative', zIndex: 10, background: 'white' }}>
        <CookiePolicy />
        <SectionContainer>
          <HeaderWrapper isMobile={isMobile} useMobileNavigation={useMobileNavigation}>
            <Logo
              setBurgerOpen={setBurgerOpen}
              setUserOpen={setUserOpen}
              registDone={registDone}
              isLogin={isLogin}
              useMobileNavigation={useMobileNavigation}
              constrainMobileLogo={isMobile || pathname.startsWith('/pbl') || /^\/(?:en|tc|sc)\/freelegallink\/?$/.test(pathname) || isExternalRosterPath(pathname)}
            />
            <Navbar
              burgerOpen={burgerOpen}
              userOpen={userOpen}
              setBurgerOpen={setBurgerOpen}
              setUserOpen={setUserOpen}
              registDone={registDone}
              isLogin={isLogin}
              accessToken={accessToken}
              layoutController={layoutController}
              showNavigation={showHeaderNavigation}
              useMobileNavigation={useMobileNavigation}
            />
          </HeaderWrapper>
        </SectionContainer>
      </nav>

      {showHeaderNavigation && useMobileNavigation && userOpen && (
        <MobileSidebarWrapper>
          <Userbar setUserOpen={setUserOpen} />
        </MobileSidebarWrapper>
      )}

      {showHeaderNavigation && useMobileNavigation && burgerOpen && (
        <MobileSidebarWrapper>
          <Sidebar setBurgerOpen={setBurgerOpen} />
        </MobileSidebarWrapper>
      )}
      {shouldRenderDesktopSidebar({
        pathname,
        isLogin: auth.isLogin,
        layoutController,
        useMobileHeader: useMobileNavigation,
      }) && <Sidebar setBurgerOpen={setBurgerOpen} />}
    </>
  );
};

export default Header;
